import { useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import type {
  ConditionKind,
  NodePath,
} from '@/components/tree-dnd/tree-dnd-types';
import {
  ADD_NODE_CATALOG,
  isConditionKind,
  type AddNodeCatalogItem,
  type AddNodeCatalogSection,
} from '@/components/tree-dnd/tree-dnd-add-catalog';
import { CONDITION_KINDS } from '@/components/tree-dnd/tree-dnd-conditions/registry';
import { useTreeDndStore } from '@/components/tree-dnd/tree-dnd-store';
import { useTreeDndUiStore } from '@/components/tree-dnd/tree-dnd-ui-store';
import { useI18nContext } from '@/i18n/i18n-react';
import { Input } from '@/components/ui/input';
import {
  Item,
  ItemContent,
  ItemDescription,
  ItemGroup,
  ItemMedia,
  ItemTitle,
} from '@/components/ui/item';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';

interface TreeDndAddSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  parentPath: NodePath;
}

const CONDITIONS_WITH_EDIT_SHEET = new Set<ConditionKind>(CONDITION_KINDS);

type CatalogLabels = {
  name: string;
  description: string;
};

function matchesQuery(query: string, labels: CatalogLabels) {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return true;

  return (
    labels.name.toLowerCase().includes(normalizedQuery) ||
    labels.description.toLowerCase().includes(normalizedQuery)
  );
}

function AddNodeSection({
  title,
  items,
  onSelect,
}: {
  title: string;
  items: Array<AddNodeCatalogItem & { labels: CatalogLabels }>;
  onSelect: (item: AddNodeCatalogItem) => void;
}) {
  if (items.length === 0) return null;

  return (
    <section className="space-y-2">
      <h3 className="px-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </h3>
      <ItemGroup className="gap-2">
        {items.map((item) => {
          const Icon = item.icon;

          return (
            <Item
              key={item.id}
              variant="outline"
              size="sm"
              className="cursor-pointer hover:bg-accent/50"
              role="button"
              tabIndex={0}
              onClick={() => onSelect(item)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onSelect(item);
                }
              }}
            >
              <ItemMedia className="size-8 text-muted-foreground [&_svg:not([class*='size-'])]:size-4">
                <Icon />
              </ItemMedia>
              <ItemContent>
                <ItemTitle>{item.labels.name}</ItemTitle>
                <ItemDescription>{item.labels.description}</ItemDescription>
              </ItemContent>
            </Item>
          );
        })}
      </ItemGroup>
    </section>
  );
}

export function TreeDndAddSheet({
  open,
  onOpenChange,
  parentPath,
}: TreeDndAddSheetProps) {
  const { LL } = useI18nContext();
  const [query, setQuery] = useState('');
  const addCondition = useTreeDndStore((state) => state.addCondition);
  const addGroup = useTreeDndStore((state) => state.addGroup);
  const requestConditionEditor = useTreeDndUiStore(
    (state) => state.requestConditionEditor
  );

  const getLabels = (id: AddNodeCatalogItem['id']): CatalogLabels => {
    if (id === 'groupAnd') {
      return {
        name: LL.audiences.filters.addSheet.items.groupAnd(),
        description: LL.audiences.filters.addSheet.descriptions.groupAnd(),
      };
    }

    if (id === 'groupOr') {
      return {
        name: LL.audiences.filters.addSheet.items.groupOr(),
        description: LL.audiences.filters.addSheet.descriptions.groupOr(),
      };
    }

    const conditionNames = {
      installedApp: LL.audiences.filters.conditions.installedApp,
      boughtProduct: LL.audiences.filters.conditions.boughtProduct,
      receivedNotification:
        LL.audiences.filters.conditions.receivedNotification,
      clickedCampaign: LL.audiences.filters.conditions.clickedCampaign,
      rfmGroup: LL.audiences.filters.conditions.rfmGroup,
      lastNotificationAt: LL.audiences.filters.conditions.lastNotificationAt,
      lastAppAccessAt: LL.audiences.filters.conditions.lastAppAccessAt,
      viewedProduct: LL.audiences.filters.conditions.viewedProduct,
      location: LL.audiences.filters.conditions.location,
      lastOrder: LL.audiences.filters.conditions.lastOrder,
      orderFrequency: LL.audiences.filters.conditions.orderFrequency,
      totalSpend: LL.audiences.filters.conditions.totalSpend,
      hasNotificationsEnabled:
        LL.audiences.filters.conditions.hasNotificationsEnabled,
    } as const;

    const conditionDescriptions = {
      installedApp: LL.audiences.filters.addSheet.descriptions.installedApp,
      boughtProduct: LL.audiences.filters.addSheet.descriptions.boughtProduct,
      receivedNotification:
        LL.audiences.filters.addSheet.descriptions.receivedNotification,
      clickedCampaign:
        LL.audiences.filters.addSheet.descriptions.clickedCampaign,
      rfmGroup: LL.audiences.filters.addSheet.descriptions.rfmGroup,
      lastNotificationAt:
        LL.audiences.filters.addSheet.descriptions.lastNotificationAt,
      lastAppAccessAt:
        LL.audiences.filters.addSheet.descriptions.lastAppAccessAt,
      viewedProduct: LL.audiences.filters.addSheet.descriptions.viewedProduct,
      location: LL.audiences.filters.addSheet.descriptions.location,
      lastOrder: LL.audiences.filters.addSheet.descriptions.lastOrder,
      orderFrequency: LL.audiences.filters.addSheet.descriptions.orderFrequency,
      totalSpend: LL.audiences.filters.addSheet.descriptions.totalSpend,
      hasNotificationsEnabled:
        LL.audiences.filters.addSheet.descriptions.hasNotificationsEnabled,
    } as const;

    return {
      name: conditionNames[id](),
      description: conditionDescriptions[id](),
    };
  };

  const filteredItems = useMemo(() => {
    return ADD_NODE_CATALOG.map((item) => ({
      ...item,
      labels: getLabels(item.id),
    })).filter((item) => matchesQuery(query, item.labels));
  }, [LL, query]);

  const itemsBySection = useMemo(() => {
    const sections: Record<AddNodeCatalogSection, typeof filteredItems> = {
      groups: [],
      filters: [],
    };

    for (const item of filteredItems) {
      sections[item.section].push(item);
    }

    return sections;
  }, [filteredItems]);

  const handleOpenChange = (nextOpen: boolean, editNodeId?: string | null) => {
    if (!nextOpen) {
      setQuery('');
      if (editNodeId) {
        requestConditionEditor(editNodeId);
      }
    }
    onOpenChange(nextOpen);
  };

  const handleSelect = (item: AddNodeCatalogItem) => {
    let editNodeId: string | undefined;

    if (item.groupOperator) {
      addGroup(parentPath, item.groupOperator);
    } else if (isConditionKind(item.id)) {
      const nodeId = addCondition(parentPath, item.id);
      if (nodeId && CONDITIONS_WITH_EDIT_SHEET.has(item.id)) {
        editNodeId = nodeId;
      }
    }

    setQuery('');
    handleOpenChange(false, editNodeId ?? null);
  };

  const hasResults =
    itemsBySection.groups.length > 0 || itemsBySection.filters.length > 0;

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full max-w-[min(100vw,26rem)] flex-col gap-0 p-0 sm:max-w-[26rem]"
      >
        <SheetHeader className="border-b px-6 py-4 text-left">
          <SheetTitle>{LL.audiences.filters.addSheet.title()}</SheetTitle>
        </SheetHeader>

        <div className="border-b px-6 py-4">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={LL.audiences.filters.addSheet.searchPlaceholder()}
              className="pl-9"
              autoFocus
            />
          </div>
        </div>

        <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto p-6">
          {hasResults ? (
            <>
              <AddNodeSection
                title={LL.audiences.filters.addSheet.sections.groups()}
                items={itemsBySection.groups}
                onSelect={handleSelect}
              />
              <AddNodeSection
                title={LL.audiences.filters.addSheet.sections.filters()}
                items={itemsBySection.filters}
                onSelect={handleSelect}
              />
            </>
          ) : (
            <p className="px-1 text-sm text-muted-foreground">
              {LL.audiences.filters.addSheet.noResults()}
            </p>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
